import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Grid, IconButton, LinearProgress, TablePagination, Tooltip } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { DateFns } from '../../../../utils/date-fns'
import { ExportHistoryRow, HistoryResponse, HistoryScope, ProfileFilterScope } from '../../gp_scopes'
import ProfileCard from './profile-card'

const LOG = Logger.get('GroupProfile-History-Table')

const rowsPerPageOptions = [10, 25, 50, 75, 100, 500]

export type GroupProfileHistoryTableProps = {
    className?: string
    style?: React.CSSProperties
    key?: string
    scope: HistoryScope
    filterScope: ProfileFilterScope
}

function GroupProfileHistoryTable({ scope, filterScope, className, style }: GroupProfileHistoryTableProps) {
    const historyResponse = scope.history || ({} as HistoryResponse<ExportHistoryRow>)

    const emitViewDocuments = (row: ExportHistoryRow) => {
        scope.onViewDocuments(row.seq!).catch(LOG.caught)
    }

    const columns = static_build_history_columns(emitViewDocuments)

    const emitRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const pageSize = parseInt(e.target.value, 10)
        filterScope.onFind({ limit: pageSize }).catch(LOG.caught)
    }

    const emitChangePage = (__e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        filterScope.onFind({ page: newPage }).catch(LOG.caught)
    }

    return (
        <ProfileCard caption="Histórico de Exportações" className={clsx(className)} style={style}>
            <Grid container spacing={2}>
                <Grid size={{ sm: 12 }} sx={{ height: 594 }}>
                    <DataGrid
                        rows={historyResponse.records || []}
                        columns={columns}
                        getRowId={static_getRowId}
                        hideFooter
                        hideFooterPagination
                        disableColumnSelector
                    />
                    {scope.history_working && <LinearProgress color="secondary" />}
                </Grid>
                <Grid size={{ sm: 12 }}>
                    {historyResponse.totalRecords > historyResponse.limit && (
                        <TablePagination
                            component="div"
                            count={historyResponse.totalRecords}
                            page={historyResponse.page}
                            onPageChange={emitChangePage}
                            rowsPerPage={historyResponse.limit || 10}
                            rowsPerPageOptions={rowsPerPageOptions}
                            onRowsPerPageChange={emitRowsPerPageChange}
                        />
                    )}
                </Grid>
            </Grid>
        </ProfileCard>
    )
}
export default GroupProfileHistoryTable

function static_build_history_columns(onView: (row: ExportHistoryRow) => void): GridColDef[] {
    return [
        {
            field: 'actions',
            headerName: 'Documentos',
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            width: 110,
            renderCell(params) {
                return (
                    <Tooltip title="Ver documentos exportados">
                        <IconButton size="small" onClick={() => onView(params.row)}>
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )
            }
        },
        {
            field: 'origem',
            headerName: 'Relatório Exportado',
            width: 250
        },
        {
            field: 'dataHora',
            headerName: 'Data Hora',
            type: 'dateTime',
            width: 150,
            valueFormatter: (value) => DateFns.formatDate(value, 'dd/MM/yyyy HH:mm')
        },
        {
            field: 'userName',
            headerName: 'Usuário',
            width: 150
        },
        {
            field: 'userStatus',
            headerName: 'Status do Usuário',
            align: 'center',
            headerAlign: 'center',
            width: 120,
            renderCell(params) {
                switch (params.value) {
                    case 'DELETED':
                        return (
                            <Tooltip title="Usuário removido do sistema">
                                <PersonRemoveIcon color="error" />
                            </Tooltip>
                        )
                    case 'INACTIVE':
                        return (
                            <Tooltip title="Usuário inativo">
                                <ClearIcon color="warning" />
                            </Tooltip>
                        )
                    case 'ACTIVE':
                        return (
                            <Tooltip title="Usuário ativo">
                                <CheckIcon color="success" />
                            </Tooltip>
                        )
                    default:
                        return (
                            <Tooltip title="Status desconhecido">
                                <span>{params.value}</span>
                            </Tooltip>
                        )
                }
            }
        },
        {
            field: 'qtdRegistros',
            headerName: 'Qtd. Exportada',
            align: 'right',
            headerAlign: 'right',
            width: 150
        },
        {
            field: 'newLimitExport',
            headerName: 'Limite Contratado',
            align: 'right',
            headerAlign: 'right',
            width: 150
        }
    ]
}

function static_getRowId(row: ExportHistoryRow) {
    return row.seq + ''
}
